import React from "react";
import { useTranslation } from "react-i18next";
import type { SettingDescriptor } from "./types";
import SettingHint from "./SettingHint";
import type { Hint } from "./useSettingsHints";

interface SettingRowProps {
  setting: SettingDescriptor;
  /** Si fourni, click "Modifier"/"Ajouter" propage. */
  onEditRequest?: (key: SettingDescriptor["key"]) => void;
  /** Row actuellement expanded — rend `editor` inline si non vide */
  expanded?: boolean;
  /** Hint actif pour cette row (PR 2) — affiché en bandeau jaune sous la value */
  hint?: Hint | null;
  /** Click sur "Utiliser « X » →" dans le bandeau hint */
  onApplySuggestion?: (key: Hint["key"], value: unknown) => void;
  /** Editeur inline à rendre quand `expanded === true` (PR 3) */
  editor?: React.ReactNode;
}

/**
 * Une row du PrePublishModal. Affiche : icon box + label/value/sub-desc + bouton.
 * En PR 1, le bouton est read-only (pas de toggle d'édition).
 * Cf. Pre-publish Modal B.html lignes 178-269.
 */
const SettingRow: React.FC<SettingRowProps> = ({
  setting,
  onEditRequest,
  expanded = false,
  hint,
  onApplySuggestion,
  editor,
}) => {
  const { t } = useTranslation("polls");
  const isHint = setting.group === "hint";
  const isImportant = setting.group === "important";
  const buttonLabel = setting.isEmpty
    ? t("prepublish_btn_add")
    : expanded
      ? t("prepublish_btn_close")
      : t("prepublish_btn_edit");

  return (
    <div
      data-testid={`setting-row-${setting.key}`}
      data-empty={setting.isEmpty ? "true" : undefined}
      data-expanded={expanded ? "true" : undefined}
      className="px-1 py-1.5"
    >
      <div className="flex items-start gap-3">
        {/* Icon box */}
        <div
          aria-hidden
          className={`flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[8px] text-[14px] ${
            isHint ? "bg-amber-500/[0.18]" : "bg-card"
          }`}
        >
          {setting.icon}
        </div>

        {/* Label + Value + Sub-description */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.3px] text-muted-foreground">
            {setting.label}
          </div>
          <div className="mt-[1px] text-[13px] leading-snug">
            <span
              className={
                setting.isEmpty
                  ? "font-medium text-muted-foreground"
                  : "font-semibold text-foreground"
              }
            >
              {setting.displayValue ??
                setting.value ??
                t("prepublish_unspecified")}
            </span>
            {setting.isDefault && !setting.isEmpty && isImportant && (
              <span
                data-testid={`setting-row-${setting.key}-default-badge`}
                className="ml-1.5 text-[9px] font-medium text-muted-foreground/40"
              >
                · {t("prepublish_default_badge")}
              </span>
            )}
          </div>
          {setting.subDescription && (
            <div className="mt-[3px] text-[11px] text-muted-foreground/40">
              {setting.subDescription}
            </div>
          )}
        </div>

        {/* Bouton droit */}
        <button
          type="button"
          data-testid={`setting-row-${setting.key}-button`}
          onClick={() => onEditRequest?.(setting.key)}
          className={
            isHint
              ? "h-8 rounded-md border border-amber-500 bg-amber-500 px-3 text-[12px] font-semibold text-[#0b0b0d] transition-colors hover:bg-amber-400"
              : "h-8 rounded-md border border-blue-400/40 bg-transparent px-3 text-[12px] font-semibold text-blue-400 transition-colors hover:bg-blue-400/10"
          }
        >
          {buttonLabel}
        </button>
      </div>
      {hint && !expanded && (
        <SettingHint hint={hint} onApplySuggestion={onApplySuggestion} />
      )}
      {expanded && editor}
    </div>
  );
};

export default SettingRow;
